import { useEffect, useRef, useState } from "react";
import { invalidPackagesCount, recalcForCount } from "@/features/ordini/packagesEditing";
import type { EditablePackage } from "@/features/ordini/packagesEditing";
import {
  fetchGiorni,
  giornoPredefinitoReso,
  oggiIso,
  type EsitoRitiroUi,
} from "@/features/ritiri/api";
import { prettifyApiError } from "@/shared/api";
import { usePermessi } from "@/shared/auth/permessi";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import type {
  AddressForm,
  CreateReturnBody,
  ReturnItem,
  ReturnPrefill,
  ReturnRecord,
  RichiestaSostituzioneBody,
} from "../api";
import { fetchInternalAddresses } from "../api";
import { openReturnBordero, openReturnLabel } from "../labelDownload";
import { AddressPicker } from "./AddressPicker";
import { AvvisaClienteTracking } from "./AvvisaClienteTracking";
import { CustomerAddressFields, INDIRIZZO_VUOTO } from "./CustomerAddressFields";
import { ResoEsistente } from "./ResoEsistente";
import { EsitoRitiroReso, SceltaRitiroReso } from "./RitiroReso";

const TITLE_ID = "return-dialog-title";

const INTERNAL_SLOTS = [
  "bscom",
  "itamoby",
  "ityhome",
  "cosma",
  "bizzotto",
  "allservice",
  "webfurniture",
] as const;

const FIELD_LABELS: Array<{ key: keyof EditablePackage; label: string }> = [
  { key: "kg", label: "kg" },
  { key: "l", label: "L cm" },
  { key: "a", label: "A cm" },
  { key: "p", label: "P cm" },
];

export interface ReturnDialogProps {
  prefill: ReturnPrefill;
  /** Record creato: quando valorizzato il dialog mostra lo stato «creato ✓»
   *  con «Scarica PDF» invece del form. Il download è coesistenza-safe
   *  (desktop openPath ↔ web HTTP) via ``openReturnLabel``. */
  created?: ReturnRecord | null;
  submitting: boolean;
  submitError: string | null;
  /** Valvola anti-deadlock: true quando la create è stata rifiutata dal gate
   *  idempotenza (esiste già un reso/sostituzione attivo per ordine+kind).
   *  Mostra l'avviso con «Annulla il precedente e ricrea». */
  conflict?: boolean;
  /** Riceve la scelta VISIBILE ORA sulla striscia (assente = «Nessun ritiro»):
   *  la striscia resta interattiva durante il conflitto, quindi non è detto
   *  sia la stessa del primo invio. */
  onCancelAndRecreate?: (ritiro?: { data: string }) => void;
  onSubmit: (body: CreateReturnBody, ritiro?: { data: string }) => void;
  /** Passo 1 della sostituzione in due tempi: registra la richiesta e basta,
   *  NESSUNA chiamata SDA. Assente → il bottone «Salva richiesta» non compare
   *  (il reso non ha due tempi: l'etichetta serve subito al cliente). */
  onSaveRequest?: (body: RichiestaSostituzioneBody) => void;
  /** Avviso tracking partito dallo stato «creato ✓»: chi ha la chat a schermo
   *  (Messaggi) la ricarica per mostrarlo. */
  onMessaggioInviato?: () => void;
  /** Esito del ritiro dal cliente (solo reso), mostrato nello stato «creato ✓». */
  ritiro?: EsitoRitiroUi | null;
  /** Una prenotazione (prima o riprova) è in volo per QUESTO reso: blocca
   *  Chiudi/backdrop/Esc (un esito in ritardo non deve arrivare a dialog già
   *  chiuso) e il tasto di riprova (un doppio click manderebbe un secondo
   *  tentativo che SDA rifiuta). */
  ritiroInCorso?: boolean;
  onRiprovaRitiro?: (data: string) => void;
  onClose: () => void;
}

export function ReturnDialog({
  prefill,
  created,
  submitting,
  submitError,
  conflict = false,
  onCancelAndRecreate,
  onSubmit,
  onSaveRequest,
  onMessaggioInviato,
  ritiro,
  ritiroInCorso = false,
  onRiprovaRitiro,
  onClose,
}: ReturnDialogProps) {
  // Il kind NON è modificabile da dentro il dialog: il prefill arriva dal
  // backend GIÀ orientato (reso → mittente cliente / destinatario fornitore;
  // sostituzione → mittente BSCom / destinatario cliente). Il vecchio toggle
  // cambiava solo il kind lasciando gli indirizzi com'erano: si emetteva una
  // LDV SDA vera con mittente e destinatario invertiti. I due kind si scelgono
  // dai tasti che aprono il dialog («Crea reso» / «Crea sostituzione»), che
  // rifanno il prefill lato server — solo il backend sa quale fornitore mettere.
  const kind = prefill.kind;
  const [sender, setSender] = useState<AddressForm>({ ...prefill.sender });
  const [recipient, setRecipient] = useState<AddressForm>({ ...prefill.recipient });
  const [senderSlot, setSenderSlot] = useState(prefill.sender_slot);
  const [recipientSlot, setRecipientSlot] = useState(prefill.recipient_slot);
  const [items] = useState<ReturnItem[]>(prefill.items);
  // «Cosa ha chiesto il cliente»: sollecitata ma NON bloccante (l'unico blocco
  // resta kg > 0 sul ramo che emette davvero). È quello che fa ricordare
  // perché la pratica esiste quando, giorni dopo, arriva il pronto merce.
  const [note, setNote] = useState("");
  // Chip indirizzo del cliente. «Altro indirizzo» SVUOTA i dieci campi invece
  // di lasciare il prefill: con il prefill un campo dimenticato manda il pacco
  // all'indirizzo vecchio, e correggere significa annullare una LDV vera.
  const [altroIndirizzo, setAltroIndirizzo] = useState(false);
  // The unit packages are the prefill packages (one per SKU); pkgs is the
  // editable state derived by recalcForCount from these.
  const unitPackages = useRef<EditablePackage[]>(prefill.packages.map((p) => ({ ...p })));
  const [pkgs, setPkgs] = useState<EditablePackage[]>(prefill.packages.map((p) => ({ ...p })));
  // Ritiro dal cliente: solo per il RESO e solo per chi scrive nell'area
  // spedizioni (il router /api/pickups). Per gli altri la striscia non c'è e il
  // reso si crea come prima, invece di un 403 a reso già creato. I giorni li
  // decide il backend (regola delle 11:50 in un posto solo); se non arrivano,
  // il reso si crea senza ritiro.
  const puoRitiri = usePermessi().puoScrivere("spedizioni");
  // «Prenota solo ritiro» ha il suo dialog sopra questo: Esc non chiude il reso.
  const [soloRitiroAperto, setSoloRitiroAperto] = useState(false);
  // I pickup SDA sono domestici e il payload di prenotazione non porta un
  // paese: qui è l'unico punto che può fermarsi PRIMA di mandarne uno
  // internazionale. Vuoto/mancante NON conta come Italia — stessa disciplina
  // dell'invoicing (``paese or "IT"`` fabbrica un dato che non c'è): un
  // country_iso assente è un «non lo so», non un «va bene».
  const mittenteItaliano = (sender.country_iso ?? "").trim().toUpperCase() === "IT";
  // SDA rifiuta un ritiro senza telefono: qui il gate decide se la scelta può
  // essere un giorno; ``SceltaRitiroReso`` mostra il motivo al posto della
  // striscia, ma senza QUESTO controllo un giorno pre-selezionato prima che
  // l'operatore tocchi qualcosa finirebbe comunque nel submit.
  const senzaTelefonoCliente = !(sender.phone ?? "").replace(/\D/g, "");
  const [giorniRitiro, setGiorniRitiro] = useState<string[]>([]);
  const [giornoRitiro, setGiornoRitiro] = useState<string | null>(null);
  useEffect(() => {
    if (kind !== "reso" || !puoRitiri) return;
    fetchGiorni()
      .then((risposta) => {
        // ``?? []``: una risposta senza giorni non deve far esplodere il dialog.
        const giorni = risposta.giorni ?? [];
        setGiorniRitiro(giorni);
        setGiornoRitiro(giornoPredefinitoReso(giorni, oggiIso()));
      })
      .catch(() => setGiorniRitiro([]));
  }, [kind, puoRitiri]);
  const conRitiro =
    kind === "reso" &&
    mittenteItaliano &&
    !senzaTelefonoCliente &&
    giorniRitiro.length > 0 &&
    giornoRitiro !== null;

  // Backend-loaded map of slot → address (single source of truth).
  const [internalAddresses, setInternalAddresses] = useState<Record<string, AddressForm>>({});
  // «Scarica PDF» dell'etichetta appena creata: ``openReturnLabel`` LANCIA su
  // 404/401, ma il call-site faceva ``void ...`` e buttava via il rifiuto —
  // l'operatore premeva e non succedeva niente, senza sapere perche'.
  const [labelBusy, setLabelBusy] = useState(false);
  const [labelError, setLabelError] = useState<string | null>(null);

  async function handleDownloadLabel(record: ReturnRecord): Promise<void> {
    setLabelBusy(true);
    setLabelError(null);
    try {
      await openReturnLabel(record);
      // BRT: il borderò esce anche come file a parte, o resta nascosto in
      // fondo all'etichetta (Davide 22/10).
      if (record.carrier === "BRT") await openReturnBordero(record);
    } catch (err) {
      setLabelError(prettifyApiError(err));
    } finally {
      setLabelBusy(false);
    }
  }

  const cancelRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    fetchInternalAddresses()
      .then(setInternalAddresses)
      .catch(() => {
        // Silently ignore — prefill addresses already populated from backend;
        // the loaded map is only needed when user switches chips manually.
      });
  }, []);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      // Una prenotazione in volo blocca anche Esc, per lo stesso motivo del
      // backdrop: vedi ``ritiroInCorso``.
      if (event.key === "Escape" && !submitting && !ritiroInCorso && !soloRitiroAperto) onClose();
    };
    window.addEventListener("keydown", onKey);
    cancelRef.current?.focus();
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, submitting, ritiroInCorso, soloRitiroAperto]);

  // The "internal" side is recipient for reso, sender for sostituzione.
  // The "customer" side is the other one.
  const internalSide = kind === "reso" ? "recipient" : "sender";

  function editAddress(side: "sender" | "recipient", field: keyof AddressForm, value: string) {
    if (side === "sender") {
      setSender((prev) => ({ ...prev, [field]: value }));
    } else {
      setRecipient((prev) => ({ ...prev, [field]: value }));
    }
  }

  function setCount(count: number) {
    setPkgs(recalcForCount(unitPackages.current, count));
  }

  function setField(index: number, field: keyof EditablePackage, value: number) {
    setPkgs((prev) =>
      prev.map((pkg, i) =>
        i === index ? { ...pkg, [field]: Number.isFinite(value) && value >= 0 ? value : 0 } : pkg,
      ),
    );
  }

  const confirmDisabled = submitting || invalidPackagesCount(pkgs) > 0;

  function handleConfirm() {
    if (confirmDisabled) return;
    const body: CreateReturnBody = {
      kind,
      order_reference: prefill.order_reference,
      sender,
      recipient,
      sender_slot: senderSlot,
      recipient_slot: recipientSlot,
      items,
      packages: pkgs,
      accessori: {},
      note,
      label_format: "PDF_A6",
    };
    if (conRitiro && giornoRitiro) onSubmit(body, { data: giornoRitiro });
    else onSubmit(body);
  }

  // Passo 1: nessun collo nel body. Il prefill propone il placeholder
  // [{kg: 0}] e kg > 0 è obbligatorio lato API: inoltrarlo darebbe 422 su una
  // richiesta che, per definizione, i colli non ce li ha ancora.
  function handleSaveRequest() {
    onSaveRequest?.({
      order_reference: prefill.order_reference,
      sender,
      recipient,
      sender_slot: senderSlot,
      recipient_slot: recipientSlot,
      items,
      accessori: {},
      note,
    });
  }

  const invalidCount = invalidPackagesCount(pkgs);
  // Due tempi solo per la sostituzione: il reso l'etichetta la vuole subito.
  const mostraSalvaRichiesta = Boolean(onSaveRequest) && kind === "sostituzione";

  // Stato «creato ✓»: dopo la creazione mostriamo la conferma + «Scarica PDF»
  // (download coesistenza-safe) invece del form. Questo rende il PDF accessibile
  // OVUNQUE il dialog sia usato — chat/ordini/ResiPage — non solo dalla lista.
  if (created) {
    // Richiesta registrata (passo 1): nessuna etichetta è stata emessa, quindi
    // niente «Scarica PDF» — offrirlo darebbe un 404 su una pratica sana.
    const soloRichiesta = created.status === "richiesta";
    const createdTitle = soloRichiesta
      ? "Richiesta registrata"
      : created.kind === "reso"
        ? "Reso creato"
        : "Sostituzione creata";
    return (
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={TITLE_ID}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
        onClick={(event) => {
          // Una prenotazione in volo non va interrotta: un esito in ritardo
          // non deve arrivare a un dialog già chiuso (vedi ``ritiroInCorso``).
          if (event.target === event.currentTarget && !ritiroInCorso) onClose();
        }}
      >
        <div className="w-full max-w-md rounded-lg border border-line bg-card shadow-popover">
          <header className="flex items-center gap-2.5 border-b border-line px-5 py-4">
            <span
              aria-hidden
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-green-soft text-ink"
            >
              <svg
                className="h-4 w-4 fill-none stroke-current stroke-[2.4] [stroke-linecap:round] [stroke-linejoin:round]"
                viewBox="0 0 24 24"
              >
                <path d="M20 6L9 17l-5-5" />
              </svg>
            </span>
            <div className="min-w-0">
              <h2 id={TITLE_ID} className="font-display text-[17px] font-bold text-ink">
                {createdTitle} ✓
              </h2>
              <p className="mt-0.5 text-[11px] text-ink-2">
                Ordine <span className="tabular-nums">{created.order_reference}</span>
              </p>
            </div>
          </header>

          <div className="px-5 py-4 space-y-3">
            <p className="text-[12.5px] leading-relaxed text-ink-2">
              {soloRichiesta
                ? "Nessuna etichetta emessa: l'etichetta si genera dopo, quando arriva il pronto merce con colli e misure."
                : created.carrier === "BRT"
                  ? "Etichetta BRT e borderò generati. «Scarica PDF» scarica l'etichetta e il borderò: l'etichetta va sui colli, il borderò all'autista BRT al ritiro."
                  : "Etichetta SDA generata. Scarica il PDF per stamparlo e applicarlo ai colli."}
            </p>
            {created.tracking_numbers.length > 0 && (
              <div>
                <p className="mb-1 text-[12px] font-semibold text-ink-2">Tracking</p>
                <ul className="space-y-0.5">
                  {created.tracking_numbers.map((t) => (
                    <li key={t} className="tabular-nums text-[12px] tabular-nums text-blue">
                      {t}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <AvvisaClienteTracking record={created} onInviato={onMessaggioInviato} />
            {created.kind === "reso" && (ritiro || ritiroInCorso) && (
              <EsitoRitiroReso
                ritiro={ritiro ?? null}
                inCorso={ritiroInCorso}
                onRiprova={onRiprovaRitiro}
              />
            )}
          </div>

          {/* Stessa forma del footer del form: banner sopra, tasti sotto. */}
          <footer className="border-t border-line px-5 py-3">
            <ErrorBanner message={labelError} className="mb-2" />
            <div className="flex items-center justify-end gap-2">
              <button
                type="button"
                onClick={onClose}
                disabled={ritiroInCorso}
                className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 disabled:cursor-not-allowed"
              >
                Chiudi
              </button>
              {!soloRichiesta && (
                <button
                  type="button"
                  disabled={labelBusy}
                  aria-busy={labelBusy}
                  onClick={() => void handleDownloadLabel(created)}
                  className="inline-flex items-center gap-[6px] rounded-sm border border-violet bg-violet px-[14px] py-2 text-[13px] font-medium text-white hover:bg-violet-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <svg
                    className="h-[14px] w-[14px] fill-none stroke-current stroke-[1.7] [stroke-linecap:round] [stroke-linejoin:round]"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M12 3v12m0 0l-4-4m4 4l4-4M5 21h14" />
                  </svg>
                  {labelBusy ? "Scarico…" : "Scarica PDF"}
                </button>
              )}
            </div>
          </footer>
        </div>
      </div>
    );
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={TITLE_ID}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={(event) => {
        if (event.target === event.currentTarget && !submitting) onClose();
      }}
    >
      <div className="w-full max-w-2xl rounded-lg border border-line bg-card shadow-popover">
        {/* Header */}
        <header className="border-b border-line px-5 py-4">
          <h2 id={TITLE_ID} className="font-display text-[17px] font-bold text-ink">
            {kind === "reso" ? "Crea reso" : "Crea sostituzione"}
          </h2>
          <p className="mt-0.5 text-[11px] text-ink-2">
            Ordine <span className="tabular-nums">{prefill.order_reference}</span>
          </p>
        </header>

        {/* Body */}
        <div className="max-h-[65vh] overflow-y-auto px-5 py-4 space-y-5">
          {/* Reso già fatto: l'etichetta ce l'ha il cliente, si prenota solo il ritiro. */}
          {kind === "reso" && puoRitiri && (
            <ResoEsistente
              orderReference={prefill.order_reference}
              onAperto={setSoloRitiroAperto}
            />
          )}
          {/* Nota: sta in CIMA perché è quella che fa ricordare, giorni dopo,
              perché la pratica esiste. Sollecitata, mai bloccante. */}
          <div>
            <label
              htmlFor="return-note"
              className="mb-1 block text-[11.5px] font-medium text-ink-2"
            >
              Cosa ha chiesto il cliente
            </label>
            <textarea
              id="return-note"
              rows={2}
              value={note}
              disabled={submitting}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Es. anta sinistra arrivata scheggiata, chiede il ricambio"
              className="w-full rounded-sm border border-line px-2 py-1.5 text-[12.5px] text-ink placeholder:text-ink-4 focus:border-line-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 disabled:opacity-50"
            />
          </div>

          {/* Addresses */}
          <div className="grid grid-cols-2 gap-4">
            {/* Sender */}
            <div className="rounded-md border border-line p-3">
              {internalSide === "sender" ? (
                <AddressPicker
                  slots={[...INTERNAL_SLOTS]}
                  value={senderSlot}
                  address={sender}
                  addressesBySlot={internalAddresses}
                  onPick={(slot) => setSenderSlot(slot)}
                  onEdit={(field, value) => editAddress("sender", field, value)}
                  label="Mittente (interno)"
                />
              ) : (
                <CustomerAddressFields
                  label="Mittente (cliente)"
                  address={sender}
                  disabled={submitting}
                  onEdit={(field, value) => editAddress("sender", field, value)}
                />
              )}
            </div>

            {/* Recipient */}
            <div className="rounded-md border border-line p-3">
              {internalSide === "recipient" ? (
                <AddressPicker
                  slots={[...INTERNAL_SLOTS]}
                  value={recipientSlot}
                  address={recipient}
                  addressesBySlot={internalAddresses}
                  onPick={(slot) => setRecipientSlot(slot)}
                  onEdit={(field, value) => editAddress("recipient", field, value)}
                  label="Destinatario (interno)"
                />
              ) : (
                <CustomerAddressFields
                  label="Destinatario (cliente)"
                  address={recipient}
                  disabled={submitting}
                  onEdit={(field, value) => editAddress("recipient", field, value)}
                  chips={
                    onSaveRequest && kind === "sostituzione"
                      ? {
                          altro: altroIndirizzo,
                          onStesso: () => {
                            setAltroIndirizzo(false);
                            setRecipient({ ...prefill.recipient });
                          },
                          onAltro: () => {
                            setAltroIndirizzo(true);
                            setRecipient({ ...INDIRIZZO_VUOTO });
                          },
                        }
                      : undefined
                  }
                />
              )}
            </div>
          </div>

          {/* Articoli */}
          <div>
            <p className="mb-1.5 text-[11.5px] font-medium text-ink-2">Articoli</p>
            <ul className="space-y-1">
              {items.map((item) => (
                <li key={item.sku} className="text-[12px] text-ink-2">
                  <span className="tabular-nums text-ink-2">{item.sku}</span>
                  {item.descrizione ? ` — ${item.descrizione}` : ""}
                  {" × "}
                  <span className="tabular-nums">{item.quantita}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Colli */}
          <div>
            <div className="mb-2 flex items-center gap-2">
              <label htmlFor="return-colli-count" className="text-[11.5px] font-medium text-ink-2">
                N° colli
              </label>
              <input
                id="return-colli-count"
                type="number"
                min={1}
                max={50}
                value={pkgs.length}
                disabled={submitting}
                onChange={(e) => setCount(Math.max(1, Number(e.target.value) || 1))}
                className="w-16 rounded-sm border border-line px-2 py-1 text-[12px] tabular-nums focus:border-line-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 disabled:opacity-50"
              />
            </div>
            <div className="space-y-1.5">
              {pkgs.map((pkg, index) => (
                <div key={index} className="flex items-center gap-2">
                  <span className="w-14 text-[11px] text-ink-2">Collo {index + 1}</span>
                  {FIELD_LABELS.map(({ key, label }) => (
                    <label key={key} className="flex items-center gap-1 text-[11px] text-ink-2">
                      <input
                        type="number"
                        min={0}
                        step={key === "kg" ? 0.1 : 1}
                        inputMode="decimal"
                        value={pkg[key]}
                        disabled={submitting}
                        aria-label={`Collo ${index + 1} ${label}`}
                        onChange={(e) => setField(index, key, Number(e.target.value))}
                        className="w-[72px] rounded-sm border border-line px-2 py-1 text-[12px] tabular-nums focus:border-line-2 focus:outline-none focus-visible:ring-1 focus-visible:ring-accent/40 disabled:opacity-50"
                      />
                      {label}
                    </label>
                  ))}
                </div>
              ))}
            </div>
          </div>
          {kind === "reso" && mittenteItaliano && giorniRitiro.length > 0 && (
            <SceltaRitiroReso
              cliente={sender}
              giorni={giorniRitiro}
              giorno={giornoRitiro}
              onGiorno={setGiornoRitiro}
              disabled={submitting}
            />
          )}
        </div>

        {/* Footer */}
        <footer className="border-t border-line px-5 py-3">
          {conflict ? (
            // Gate idempotenza (service.py): un reso ATTIVO per questo
            // ordine+kind blocca la ricreazione. La valvola annulla il
            // precedente e ritenta, così l'utente non resta in un vicolo cieco.
            <div
              role="alert"
              className="mb-2 rounded-sm border border-amber/30 bg-amber-soft px-2.5 py-2 text-[11.5px] text-ink"
            >
              <p className="mb-1.5 leading-snug">
                Esiste già un {kind} attivo per questo ordine: annulla il precedente per rigenerare
                l'etichetta.
              </p>
              <button
                type="button"
                onClick={() =>
                  onCancelAndRecreate?.(
                    conRitiro && giornoRitiro ? { data: giornoRitiro } : undefined,
                  )
                }
                disabled={submitting}
                className="inline-flex items-center gap-1.5 rounded-sm border border-violet bg-violet px-2.5 py-[5px] text-[11.5px] font-medium text-white hover:bg-violet-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Ricreazione in corso…" : "Annulla il precedente e ricrea"}
              </button>
            </div>
          ) : (
            <ErrorBanner message={submitError} className="mb-2" />
          )}
          {invalidCount > 0 && (
            <p className="mb-2 text-[11.5px] text-red">
              {invalidCount} coll{invalidCount === 1 ? "o" : "i"} senza peso: inserisci un peso
              maggiore di zero per confermare.
            </p>
          )}
          <div className="flex items-center justify-end gap-2">
            <button
              ref={cancelRef}
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper disabled:cursor-not-allowed disabled:opacity-50"
            >
              Annulla
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={confirmDisabled}
              aria-busy={submitting}
              className={
                mostraSalvaRichiesta
                  ? "rounded-sm border border-line px-[14px] py-2 text-[13px] font-medium text-ink-2 hover:bg-paper disabled:cursor-not-allowed disabled:opacity-50"
                  : "rounded-sm border border-violet bg-violet px-[14px] py-2 text-[13px] font-medium text-white hover:bg-violet-2 disabled:cursor-not-allowed disabled:opacity-50"
              }
            >
              {submitting ? (
                <>
                  <svg
                    className="mr-1.5 inline h-[13px] w-[13px] animate-spin"
                    viewBox="0 0 24 24"
                    fill="none"
                    aria-hidden="true"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                    />
                  </svg>
                  Creazione in corso…
                </>
              ) : conRitiro ? (
                "Crea reso e prenota il ritiro"
              ) : (
                `Crea ${kind} e genera etichetta`
              )}
            </button>
            {mostraSalvaRichiesta && (
              <button
                type="button"
                onClick={handleSaveRequest}
                disabled={submitting}
                aria-busy={submitting}
                className="rounded-sm border border-violet bg-violet px-[14px] py-2 text-[13px] font-medium text-white hover:bg-violet-2 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {submitting ? "Salvataggio…" : "Salva richiesta"}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
